// src/utils/sessionGuard.js
//
// NZSC-3650: 로그인 필요 라우트 가드의 세션 판정.
//
// 배경 — 기존 가드는 `if (localStorage.naezip_to_ac_se) next(); else next("/")` 로 토큰의
// "존재"만 그 자리에서 확인했다. 그런데 api-public 의 /user/auth/refresh 는 호출마다
// rotation + 이전 토큰 즉시 무효화이고 grace 가 없어(auth.controller.ts refresh), 두 곳이
// 겹쳐 refresh 하면 늦은 쪽이 401 을 받는다 — 쿠키·세션은 유효한데도. 그 401 을 받은 쪽이
// 토큰을 지우면 "로그인은 살아있는데 localStorage 만 잠깐 빈" 창이 생기고, 하필 그 찰나에
// 가드가 판정하면 홈("/")으로 튕긴다. 이용권 사용 API 는 이미 성공한 뒤라 이용권만 빠지고
// 완료 화면은 못 보는 사고가 된다(order 413620 실측, 2026-08-21).
//
// 그래서 판정을 "토큰이 있는가" → "세션을 확보할 수 있는가"로 바꾼다. 없으면 refresh 쿠키로
// 1회 복구를 시도한 뒤에 판정한다. 이는 naezipscan-web-core src/lib/session.ts 의
// bootstrapSession() 과 같은 규약이며, 락 이름도 같은 값을 써서 두 앱이 **같은 뮤텍스**에
// 참여하게 한다(기존에는 web-core 만 락을 잡아 Vue↔Next refresh 레이스가 남아 있었다).
//
// refresh 는 api.js 인스턴스를 쓰지 않고 별도 authApi 인스턴스로 호출한다 — api.js 응답 인터셉터는
// refresh 엔드포인트의 401 을 전체 로그아웃(localStorage.clear + 리로드)으로 처리하므로,
// 레이스에서 진 것뿐인 401 로 세션을 통째로 날리면 안 된다.

import { requestRefreshToken } from "./authApi";
import {
  captureRefreshSession,
  commitRefreshSession,
  isRefreshSessionCurrent,
} from "./authSession";

const TOKEN_KEY = "naezip_to_ac_se";
const REFRESH_LOCK = "nzsc-session-refresh"; // web-core src/lib/session.ts 와 같은 이름 유지
const REFRESH_FLOW_TIMEOUT_MS = 12000;

export const getAccessToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    return null;
  }
};

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const withTabLock = async fn => {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request(REFRESH_LOCK, fn);
  }
  return fn();
};

const requestRefresh = async (signal, snapshot) => {
  try {
    const res = await requestRefreshToken({ signal });
    const body = res?.data;
    const session = body?.data?.session;
    if (signal.aborted || !commitRefreshSession(snapshot, session)) return null;
    return session;
  } catch (error) {
    return null;
  }
};

let refreshing = null;

/** single-flight refresh — 동시 호출이 있어도 실제 요청은 1회. rotation 소모를 막는다. */
export const refreshAccessToken = () => {
  if (!refreshing) {
    refreshing = (async () => {
      const snapshot = captureRefreshSession();
      const controller = new AbortController();
      let timeoutId;
      const timeout = new Promise(resolve => {
        timeoutId = setTimeout(() => {
          controller.abort();
          resolve(null);
        }, REFRESH_FLOW_TIMEOUT_MS);
      });
      try {
        const work = withTabLock(async () => {
          // 대기 중 세션이 바뀌면 이전 요청은 종료한다. 새 세션의 요청이 다시 진입한다.
          if (controller.signal.aborted || !isRefreshSessionCurrent(snapshot)) return null;

          const first = await requestRefresh(controller.signal, snapshot);
          if (first) return first;
          if (controller.signal.aborted || !isRefreshSessionCurrent(snapshot)) return null;

          // 실패 ≠ 세션 만료: rotation 레이스에서 진 것일 수 있다
          await sleep(200 + Math.random() * 300);
          if (controller.signal.aborted || !isRefreshSessionCurrent(snapshot)) return null;
          return requestRefresh(controller.signal, snapshot);
        });
        return await Promise.race([work, timeout]);
      } finally {
        clearTimeout(timeoutId);
        refreshing = null;
      }
    })();
  }
  return refreshing;
};

/** 토큰이 있으면 그대로, 없으면 refresh 쿠키로 1회 복구해서 반환. 없으면 null. */
export const ensureSession = async () => {
  const token = getAccessToken();
  if (token) return token;
  return (await refreshAccessToken()) || getAccessToken();
};

/**
 * 로그인 필요 라우트용 beforeEnter.
 * 기존 `if (localStorage.naezip_to_ac_se) next(); else next("/")` 의 대체 —
 * 판정만 세션 복구 1회를 거치고, 통과/차단 동작은 원본과 동일하다.
 */
export const requireSession = async (to, from, next) => {
  if (await ensureSession()) {
    next();
    return;
  }
  next("/");
};
